import React, { useRef, useEffect } from 'react';
import './ScrollStrokeBox.css';

/**
 * ScrollStrokeBox
 * Wraps children in a container with a 4-colour glowing SVG border
 * that draws itself as the element scrolls into view.
 */
const ScrollStrokeBox = ({ children, borderRadius = 20 }) => {
  const containerRef = useRef(null);
  const strokeRef = useRef(null);
  const pathLengthRef = useRef(0);

  useEffect(() => {
    const strokeEl = strokeRef.current;
    const containerEl = containerRef.current;
    if (!strokeEl || !containerEl) return;

    const updatePathLength = () => {
      const pl = strokeEl.getTotalLength();
      pathLengthRef.current = pl;
      strokeEl.style.setProperty('--path-length', pl);
      strokeEl.style.strokeDasharray = pl;
      strokeEl.style.strokeDashoffset = pl; // Start hidden
    };

    const handleScroll = () => {
      const rect = containerEl.getBoundingClientRect();
      const windowHeight = window.innerHeight;
      const visiblePixels = windowHeight - rect.top;
      const drawDistance = Math.min(rect.height, windowHeight * 0.75);
      let pct = visiblePixels / drawDistance;
      pct = Math.max(0, Math.min(1, pct));
      const drawLength = pathLengthRef.current * (1 - pct);
      strokeEl.style.strokeDashoffset = drawLength;
    };

    // Small delay to ensure the SVG is fully laid out
    const timer = setTimeout(() => {
      updatePathLength();
      window.addEventListener('scroll', handleScroll, { passive: true });
      window.addEventListener('resize', updatePathLength, { passive: true });
      handleScroll(); // Run once in case already visible
    }, 200);

    return () => {
      clearTimeout(timer);
      window.removeEventListener('scroll', handleScroll);
      window.removeEventListener('resize', updatePathLength);
    };
  }, []);

  return (
    <div ref={containerRef} className="scroll-stroke-box-container">
      {/* Children sit on top */}
      <div className="scroll-stroke-box-content">
        {children}
      </div>

      {/* Animated glowing SVG border — sits behind children */}
      <svg
        className="scroll-stroke-svg"
        width="100%"
        height="100%"
        xmlns="http://www.w3.org/2000/svg"
      >
        <defs>
          <linearGradient id="multiGradient" x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%"   stopColor="#006EFD" />
            <stop offset="33%"  stopColor="#17D1C6" />
            <stop offset="66%"  stopColor="#7356BF" />
            <stop offset="100%" stopColor="#FF7D49" />
          </linearGradient>

          <filter id="strokeGlow" x="-20%" y="-20%" width="140%" height="140%">
            <feGaussianBlur stdDeviation="5" result="blur" />
            <feMerge>
              <feMergeNode in="blur" />
              <feMergeNode in="blur" />
              <feMergeNode in="SourceGraphic" />
            </feMerge>
          </filter>
        </defs>

        <rect
          ref={strokeRef}
          className="scroll-stroke-rect"
          x="2"
          y="2"
          width="calc(100% - 4px)"
          height="calc(100% - 4px)"
          rx={borderRadius}
          ry={borderRadius}
        />
      </svg>
    </div>
  );
};

export default ScrollStrokeBox;
